import React, { useState, useEffect } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Eye } from "lucide-react";





// ---------------------------------------------------------------------------
// Iteration 44 — Service Visibility Toggle
// ---------------------------------------------------------------------------
// Admin can hide/show services from the public booking catalogue. Hidden
// services stay resolvable by ID (so existing bookings/invoices keep working)
// but are filtered out of `GET /services`. Overrides live in the
// `service_visibility` Mongo collection so they survive deploys.
const AdminServiceVisibility = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/services");
      setItems(r.data || []);
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر تحميل الخدمات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (svc, next) => {
    setPendingId(svc.id);
    // Optimistic update so the switch feels instant
    setItems((prev) => prev.map((s) => (s.id === svc.id ? { ...s, is_active: next } : s)));
    try {
      await api.patch(`/admin/services/${svc.id}/visibility`, { is_active: next });
      toast.success(
        next
          ? `تم إظهار «${svc.title_ar}» في صفحة الحجز`
          : `تم إخفاء «${svc.title_ar}» من صفحة الحجز`,
      );
    } catch (e) {
      // Rollback on failure
      setItems((prev) => prev.map((s) => (s.id === svc.id ? { ...s, is_active: !next } : s)));
      toast.error(extractApiErrorMessage(e) || "تعذّر حفظ الحالة");
    } finally {
      setPendingId(null);
    }
  };

  const groupLabel = (s) => {
    if (s.type === "book") return "الكتب الرقمية";
    if (s.modality === "voice") return "الجلسات الصوتية";
    if (s.modality === "text") return "جلسات الرسائل";
    return "أخرى";
  };
  const grouped = items.reduce((acc, s) => {
    const k = groupLabel(s);
    acc[k] = acc[k] ? [...acc[k], s] : [s];
    return acc;
  }, {});

  return (
    <div data-testid="admin-service-visibility" dir="rtl" className="space-y-6">
      <div className="card-warm p-5">
        <div className="flex items-center gap-2 mb-1">
          <Eye className="w-5 h-5 text-[#0A4D3C]" />
          <h3 className="font-heading text-xl text-[#222222]">الجلسات والخدمات</h3>
        </div>
        <p className="text-sm text-[#5E5C58] leading-relaxed">
          فعّلي أو أوقفي ظهور أي جلسة من صفحة الحجز العامة. الإيقاف يُخفي
          الخدمة من القائمة المعروضة للعميلات فورًا (بدون انتظار النشر) لكنه
          يحافظ على عمل الحجوزات والفواتير الموجودة مسبقًا.
        </p>
      </div>

      {loading ? (
        <div className="card-warm p-8 text-center text-sm text-[#5E5C58]">
          ...جارٍ التحميل
        </div>
      ) : (
        Object.entries(grouped).map(([label, list]) => (
          <div key={label} className="card-warm p-0 overflow-hidden">
            <div className="bg-[#F6F4EE] px-5 py-3 text-sm font-semibold text-[#5E5C58]">
              {label} ({list.length})
            </div>
            <div className="divide-y divide-[#E5E1D8]">
              {list.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-4 px-5 py-4"
                  data-testid={`service-row-${s.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-[#222222]">{s.title_ar}</span>
                      <Badge
                        className={`rounded-full text-xs ${
                          s.is_active
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-rose-100 text-rose-800"
                        }`}
                        data-testid={`service-status-${s.id}`}
                      >
                        {s.is_active ? "ظاهرة" : "مخفية"}
                      </Badge>
                    </div>
                    {s.subtitle_ar && (
                      <div className="text-xs text-[#5E5C58]">{s.subtitle_ar}</div>
                    )}
                    <div className="text-xs text-[#8B8780] mt-1 font-mono" dir="ltr">
                      {s.id} · {s.price?.toLocaleString?.() || s.price} ر.س
                    </div>
                  </div>
                  <Switch
                    checked={s.is_active}
                    disabled={pendingId === s.id}
                    onCheckedChange={(next) => toggle(s, next)}
                    data-testid={`service-toggle-${s.id}`}
                    aria-label={`تفعيل/إيقاف ${s.title_ar}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export { AdminServiceVisibility };
